import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PersonAddIcon from "@mui/icons-material/PersonAddOutlined";
import Avatar from "@mui/material/Avatar";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import format from "date-fns/format";
import subDays from "date-fns/subDays";
import { Autocomplete } from "@mui/material";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

// Custom hooks
import useAttendanceData from "layouts/tables/data/attendanceTableData";

function Attendance() {
  const {
    columns,
    rows,
    isLoading,
    error,
    showOnlyUnchecked,
    setShowOnlyUnchecked,
    showAbsentMembers,
    handleShowAbsent,
    selectedDate,
    handleDateChange,
    fetchAttendanceData,
    // Manual attendance properties
    manualAttendanceDialog,
    setManualAttendanceDialog,
    selectedMember,
    setSelectedMember,
    searchQuery,
    setSearchQuery,
    filteredMembers,
    takeManualAttendance,
  } = useAttendanceData();

  const [dateRange, setDateRange] = React.useState({
    startDate: subDays(new Date(), 365),
    endDate: new Date(),
  });

  const handleStartDateChange = (date) => {
    const newDateRange = { ...dateRange, startDate: date };
    setDateRange(newDateRange);
    fetchAttendanceData(date, dateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = { ...dateRange, endDate: date };
    setDateRange(newDateRange);
    fetchAttendanceData(dateRange.startDate, date);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDateRange({ startDate: today, endDate: today });
    fetchAttendanceData(today, today);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const closeManualAttendanceDialog = () => {
    setManualAttendanceDialog(false);
    setSelectedMember(null);
    setSearchQuery("");
  };

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading Attendances...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title={`Church Attendance - ${formatDisplayDate(dateRange.startDate)}${
          dateRange.startDate.getTime() !== dateRange.endDate.getTime()
            ? ` to ${formatDisplayDate(dateRange.endDate)}`
            : ""
        }`}
        subtitle="Track member check-ins and manage attendance records."
        actions={
          <>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={dateRange.startDate}
                onChange={handleStartDateChange}
                maxDate={dateRange.endDate}
                renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
              />
              <DatePicker
                label="To Date"
                value={dateRange.endDate}
                onChange={handleEndDateChange}
                minDate={dateRange.startDate}
                renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
              />
            </LocalizationProvider>
            <Button variant="outlined" onClick={handleTodayClick}>
              Today
            </Button>
            <Button
              variant={showOnlyUnchecked ? "contained" : "outlined"}
              onClick={() => setShowOnlyUnchecked(!showOnlyUnchecked)}
            >
              {showOnlyUnchecked ? "Show All" : "Show Only Unchecked"}
            </Button>
            <Button
              variant={showAbsentMembers ? "contained" : "outlined"}
              onClick={handleShowAbsent}
            >
              {showAbsentMembers ? "Hide Absent" : "Show Absent"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => setManualAttendanceDialog(true)}
            >
              Manual Attendance
            </Button>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted={false}
          entriesPerPage={true}
          showTotalEntries={true}
          noEndBorder
          canSearch={true}
          searchLabel="Search Members..."
        />
      </Card>

      {/* Manual Attendance Dialog */}
      <FormDialog
        open={manualAttendanceDialog}
        onClose={closeManualAttendanceDialog}
        title="Mark Manual Attendance"
        onSave={takeManualAttendance}
        saveLabel="Mark Present"
        saveDisabled={!selectedMember}
        maxWidth="sm"
      >
        <FormSection>
          <Autocomplete
            options={filteredMembers}
            getOptionLabel={(option) => `${option.first_name} ${option.surname}`}
            value={selectedMember}
            onChange={(event, newValue) => setSelectedMember(newValue)}
            inputValue={searchQuery}
            onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Search Member" required fullWidth margin="normal" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {option.first_name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="button" fontWeight={600} display="block">
                      {option.first_name} {option.surname}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {option.phone} • {option.email}
                    </Typography>
                  </Box>
                </Box>
              </li>
            )}
          />
          {selectedMember && (
            <Box mt={2} p={1.5} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2">
                Selected:{" "}
                <strong>
                  {selectedMember.first_name} {selectedMember.surname}
                </strong>
              </Typography>
            </Box>
          )}
        </FormSection>
      </FormDialog>
    </Box>
  );
}

export default Attendance;
